'use client';

import { c as _c } from "react/compiler-runtime";
import { Spinner } from '@common/components';
import { cn } from '@core/utils';
import type { QueryKey } from '@tanstack/react-query';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import React, { useState } from 'react';
interface InfiniteListWithMoreButtonProps<TData> {
  /**
   * React Query에서 사용할 쿼리 키
   * 이 키를 기준으로 쿼리 캐싱 및 무효화가 이루어짐
   */
  queryKey: QueryKey;
  /**
   * 데이터를 가져오는 비동기 함수
   * @param pageParam - 현재 페이지 파라미터(오프셋)
   * @returns Promise<TData[]> - 데이터 배열을 반환하는 Promise
   */
  queryFn: ({
    pageParam
  }: {
    pageParam: number;
  }) => Promise<TData[]>;
  /**
   * 각 아이템을 렌더링하는 함수
   * @param item - 렌더링할 아이템 데이터
   * @param index - 아이템의 인덱스
   * @returns ReactNode - 렌더링할 컴포넌트
   */
  renderItem: (item: TData, index: number) => ReactNode;
  /**
   * 각 아이템의 고유 키를 생성하는 함수
   * 제공되지 않을 경우 기본적으로 item.id 또는 index를 사용
   * @param item - 키를 생성할 아이템 데이터
   * @param index - 아이템의 인덱스
   * @returns string | number - 고유 키 값
   */
  getItemKey: (item: TData, index: number) => string | number;
  /**
   * 한 페이지에 표시할 아이템 수
   */
  pageSize: number;
  /**
   * 로딩 상태를 표시하는 컴포넌트
   */
  loadingComponent?: ReactNode;
  /**
   * 추가 데이터 로딩 상태를 표시하는 컴포넌트
   */
  fetchingNextPageComponent?: ReactNode;
  /**
   * 더보기 버튼
   */
  moreButtonComponent: ReactNode;
  moreButtonClassName?: string;
  /**
   * 초기 데이터
   * 서버/초기 렌더링 시 HTML에 포함
   */
  initialData?: TData[];
  /**
   * 조회 결과가 0건일 때 표시할 컴포넌트
   */
  emptyComponent?: ReactNode;
  //* 쿼리 실행 여부(기본값 true)
  enabled?: boolean;
  //* 목록 재사용 시간(ms) — 생략하면 전역 기본값
  staleTime?: number;
}
export const InfiniteListWithMoreButton = t0 => {
  const $ = _c(50);
  const {
    queryKey,
    queryFn,
    renderItem,
    getItemKey,
    pageSize,
    loadingComponent,
    fetchingNextPageComponent,
    moreButtonComponent,
    moreButtonClassName,
    initialData,
    emptyComponent,
    enabled: t1,
    staleTime
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const [initialQueryKey] = useState(queryKey);
  const isQueryKeyChanged = JSON.stringify(queryKey) !== JSON.stringify(initialQueryKey);
  const shouldUseInitialData = !isQueryKeyChanged ? initialData : undefined;
  let t2;
  if ($[0] !== queryFn) {
    t2 = t3 => {
      const {
        pageParam
      } = t3;
      return queryFn({
        pageParam
      });
    };
    $[0] = queryFn;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== pageSize) {
    t3 = (lastPage, _allPages, lastPageParam) => {
      if (!lastPage || lastPage.length === 0 || lastPage.length < pageSize) {
        return;
      }
      return lastPageParam + 1;
    };
    $[2] = pageSize;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== initialData || $[5] !== shouldUseInitialData) {
    t4 = shouldUseInitialData ? {
      pages: [initialData],
      pageParams: [1]
    } : undefined;
    $[4] = initialData;
    $[5] = shouldUseInitialData;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== enabled || $[8] !== queryKey || $[9] !== staleTime || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = {
      queryKey,
      queryFn: t2,
      getNextPageParam: t3,
      initialPageParam: 1,
      placeholderData: keepPreviousData,
      initialData: t4,
      enabled,
      staleTime
    };
    $[7] = enabled;
    $[8] = queryKey;
    $[9] = staleTime;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    isPlaceholderData
  } = useInfiniteQuery(t5);
  let t6;
  if ($[14] !== data?.pages) {
    t6 = data?.pages?.flat().filter(_temp) ?? [];
    $[14] = data?.pages;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const loadedItems = t6;
  const isWaitingToFetch = !enabled && loadedItems.length === 0;
  const isEmpty = !isLoading && !isWaitingToFetch && !isError && !isPlaceholderData && loadedItems.length === 0;
  let t7;
  if ($[16] !== getItemKey || $[17] !== renderItem || $[18] !== shouldUseInitialData) {
    t7 = shouldUseInitialData?.map((item_0, index) => <React.Fragment key={getItemKey(item_0, index)}>{renderItem(item_0, index)}</React.Fragment>);
    $[16] = getItemKey;
    $[17] = renderItem;
    $[18] = shouldUseInitialData;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== data?.pages || $[21] !== getItemKey || $[22] !== renderItem || $[23] !== shouldUseInitialData) {
    t8 = data?.pages?.slice(shouldUseInitialData ? 1 : 0).flat().filter(_temp2).map((item_2, index_0) => <React.Fragment key={getItemKey(item_2, index_0)}>{renderItem(item_2, index_0)}</React.Fragment>);
    $[20] = data?.pages;
    $[21] = getItemKey;
    $[22] = renderItem;
    $[23] = shouldUseInitialData;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== isLoading || $[26] !== isWaitingToFetch || $[27] !== loadingComponent) {
    t9 = (isLoading || isWaitingToFetch) && (loadingComponent || <div className="flex-center" style={{
      gridColumn: "1 / -1"
    }}><Spinner /></div>);
    $[25] = isLoading;
    $[26] = isWaitingToFetch;
    $[27] = loadingComponent;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== fetchingNextPageComponent || $[30] !== isFetchingNextPage) {
    t10 = isFetchingNextPage && (fetchingNextPageComponent || <div className="flex-center" style={{
      gridColumn: "1 / -1"
    }}><Spinner /></div>);
    $[29] = fetchingNextPageComponent;
    $[30] = isFetchingNextPage;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== fetchNextPage || $[33] !== hasNextPage || $[34] !== moreButtonClassName || $[35] !== moreButtonComponent) {
    t11 = hasNextPage !== false && React.isValidElement(moreButtonComponent) && <div className={cn("flex justify-center", moreButtonClassName)} style={{
      gridColumn: "1 / -1"
    }}>{React.cloneElement(moreButtonComponent as React.ReactElement<Record<string, unknown>>, {
        onClick: fetchNextPage
      })}</div>;
    $[32] = fetchNextPage;
    $[33] = hasNextPage;
    $[34] = moreButtonClassName;
    $[35] = moreButtonComponent;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== emptyComponent || $[38] !== isEmpty) {
    t12 = isEmpty && emptyComponent ? <div style={{
      gridColumn: "1 / -1"
    }}>{emptyComponent}</div> : null;
    $[37] = emptyComponent;
    $[38] = isEmpty;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== isError) {
    t13 = isError && <div className="p-16 text-center text-R-500" style={{
      gridColumn: "1 / -1"
    }}>목록을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.</div>;
    $[40] = isError;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t10 || $[43] !== t11 || $[44] !== t12 || $[45] !== t13 || $[46] !== t7 || $[47] !== t8 || $[48] !== t9) {
    t14 = <>{t7}{t8}{t9}{t10}{t11}{t12}{t13}</>;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
    $[46] = t7;
    $[47] = t8;
    $[48] = t9;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  return t14;
};
function _temp(item) {
  return item !== undefined;
}
function _temp2(item_1) {
  return item_1 !== undefined;
}